/**
 * TaxProfileForm — Sprint 1, Feb 2026.
 *
 * Wiederverwendbares Formular für die steuerlichen Stammdaten:
 *   - Identität (Firma, Inhaber, Adresse)
 *   - Steuer-Identifier (StNr, USt-IdNr)
 *   - Finanzamt
 *   - Versteuerung (Ist/Soll, EÜR/Bilanz, Tätigkeit, UVA-Frequenz, ZM)
 *   - Bank
 *
 * Country-aware: zeigt je nach `country_code` die passenden Labels und Hilfetexte.
 *
 * Props:
 *   - profile        — { tax_number, vat_id, ... country_code }
 *   - countryConfig  — output of /api/country-config/{code}
 *   - onSave(payload) async
 *   - readOnly       — render fields disabled
 *   - hideIdentity   — hide name/address (wenn anderswo gepflegt)
 */
import React, { useEffect, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Save, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import FachInfo from '@/components/ui/fach-info';

const SECTION = 'mb-6';
const FIELD_GRID = 'grid grid-cols-1 sm:grid-cols-2 gap-3';

// ─────────────── Country-aware labels & hints ───────────────
const COUNTRY_LABELS = {
  DE: {
    tax_number: 'Steuernummer',
    tax_number_placeholder: '21/815/08150',
    vat_id: 'USt-IdNr.',
    vat_id_placeholder: 'DE123456789',
    business_freelance: 'Freiberuflich (Anlage S)',
    business_commercial: 'Gewerblich (Anlage G)',
    accounting_eur: 'Einnahmen-Überschuss-Rechnung (§4(3) EStG)',
    accounting_balance: 'Bilanzierung (§4(1)/§5 EStG)',
    accounting_ea: null,
    company_register_label: 'Handelsregister-Nr. (optional)',
  },
  AT: {
    tax_number: 'Steuernummer',
    tax_number_placeholder: '12 345/6789',
    vat_id: 'UID-Nummer',
    vat_id_placeholder: 'ATU12345678',
    business_freelance: 'Selbständig (E1a)',
    business_commercial: 'Gewerbe (E1)',
    accounting_eur: null,
    accounting_balance: 'Doppelte Buchhaltung',
    accounting_ea: 'Einnahmen-Ausgaben-Rechnung',
    company_register_label: 'Firmenbuchnummer (FN)',
  },
  CH: {
    tax_number: 'Steuernummer',
    tax_number_placeholder: 'optional',
    vat_id: 'UID-Nummer',
    vat_id_placeholder: 'CHE-123.456.789 MWST',
    business_freelance: 'Selbständige Erwerbstätigkeit',
    business_commercial: 'Gewerbe',
    accounting_eur: null,
    accounting_balance: 'Doppelte Buchhaltung',
    accounting_ea: 'Einfache Buchhaltung',
    company_register_label: 'UID-Nummer',
  },
};

const VAT_FREQ_LABELS = {
  monthly: 'Monatlich',
  quarterly: 'Vierteljährlich',
  yearly: 'Jährlich',
  exempt: 'Befreit',
};

// ─────────────── Steuernummer-Format pro Land (für UVA/UStVA erforderlich) ───────────────
// DE: Landes-Steuernummer NN/NNN/NNNNN (10–11 Ziffern) — NICHT die USt-IdNr.
// AT: 9-stellige FASTNR (Finanzamts-/Steuernummer) für FinanzOnline, z.B. 12 345/6789.
// CH: keine strikte Anforderung (optional).
const TAX_NUMBER_RULES = {
  DE: { re: /^\d{2,3}\/\d{2,4}\/\d{4,5}$/, format: '21/815/08150',
        hint: 'Steuernummer im Landesformat (z.B. 21/815/08150) — nicht die USt-IdNr.' },
  AT: { re: /^\d{2}[\s-]?\d{3}\/\d{4}$/, format: '12 345/6789',
        hint: '9-stellige Finanzamts-/Steuernummer (FASTNR) für FinanzOnline, z.B. 12 345/6789 — nicht die UID-Nummer.' },
  CH: null,
};

function validateTaxNumber(value, cc) {
  const v = (value || '').trim();
  if (!v) return null; // Leer erlaubt — fehlende Nummer wird im Pre-Flight gesondert gemeldet.
  const rule = TAX_NUMBER_RULES[cc];
  if (!rule) return null;
  return rule.re.test(v) ? null : `Ungültiges Format. Erwartet: ${rule.format}`;
}

function Section({ title, children, dataTestid }) {
  return (
    <div className={SECTION} data-testid={dataTestid}>
      <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">{title}</h3>
      {children}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-xs font-medium text-slate-700 mb-1 block">{label}</Label>
      {children}
      {hint && <p className="text-[11px] text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

function Select({ value, onChange, options, disabled, testid }) {
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value || null)}
      disabled={disabled}
      data-testid={testid}
      className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-md focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 disabled:bg-zinc-50 disabled:text-slate-500"
    >
      <option value="">— bitte wählen —</option>
      {options.filter((o) => o.label).map((o) => (
        <option key={o.value} value={o.value}>{o.label}</option>
      ))}
    </select>
  );
}

export function TaxProfileForm({ profile, countryConfig, onSave, readOnly = false, hideIdentity = false }) {
  const [draft, setDraft] = useState(profile || {});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setDraft(profile || {});
    setDirty(false);
  }, [profile]);

  const cc = (draft.country_code || 'DE').toUpperCase();
  const labels = COUNTRY_LABELS[cc] || COUNTRY_LABELS.DE;
  const taxNumberError = validateTaxNumber(draft.tax_number, cc);

  const set = (key) => (val) => {
    setDraft((d) => ({ ...d, [key]: val }));
    setDirty(true);
  };
  const setEv = (key) => (e) => set(key)(e.target.value);

  const accOptions = [
    { value: 'eur', label: labels.accounting_eur },
    { value: 'ea_rechnung', label: labels.accounting_ea },
    { value: 'balance', label: labels.accounting_balance },
  ];

  const submit = async () => {
    if (taxNumberError) {
      toast.error(`Steuernummer: ${taxNumberError}`);
      return;
    }
    setSaving(true);
    try {
      // Nur die geänderten Profil-Felder mitschicken — kein Country-Code aus dem Form (separater Flow)
      const payload = { ...draft };
      delete payload._country_label;
      delete payload._country_flag;
      delete payload._voranmeldung_label;
      delete payload._voranmeldung_authority;
      await onSave(payload);
      // C/C-Code-Review-Fix (Sprint 1, Iter63): Dirty-State auch im SB-Pfad zuverlässig
      // resetten, statt nur per useEffect-Triggered durch profile-Prop-Change. Das schützt
      // gegen Race-Conditions, wenn der Caller (z.B. SB-MasterDataTab) das `profile` per
      // load() asynchron nachholt und der Form-State sich auf das alte profile-Snapshot
      // berufen würde.
      setDirty(false);
      toast.success('Stammdaten gespeichert');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-6" data-testid="tax-profile-form">
      {/* Identity */}
      {!hideIdentity && (
        <Section title="Mandant" dataTestid="tax-section-identity">
          <div className={FIELD_GRID}>
            <Field label="Firmenname">
              <Input value={draft.company_name || ''} onChange={setEv('company_name')} disabled={readOnly} data-testid="tp-company-name" />
            </Field>
            <Field label="Inhaber">
              <Input value={draft.owner_name || ''} onChange={setEv('owner_name')} disabled={readOnly} data-testid="tp-owner-name" />
            </Field>
            <Field label="Straße + Nr.">
              <Input value={`${draft.street || ''}${draft.street_number ? ' ' + draft.street_number : ''}`} onChange={(e) => {
                const parts = e.target.value.trim().split(/\s+/);
                if (parts.length > 1) {
                  set('street_number')(parts.pop());
                  set('street')(parts.join(' '));
                } else {
                  set('street')(e.target.value);
                }
              }} disabled={readOnly} data-testid="tp-street" />
            </Field>
            <Field label="PLZ + Ort">
              <div className="flex gap-2">
                <Input value={draft.postal_code || ''} onChange={setEv('postal_code')} disabled={readOnly} className="w-24" data-testid="tp-zip" />
                <Input value={draft.city || ''} onChange={setEv('city')} disabled={readOnly} className="flex-1" data-testid="tp-city" />
              </div>
            </Field>
          </div>
        </Section>
      )}

      {/* Steuer-Identifier */}
      <Section title={`Steuer-Identifikation${countryConfig ? ` · ${countryConfig.flag} ${countryConfig.name}` : ''}`} dataTestid="tax-section-ids">
        <div className={FIELD_GRID}>
          <Field label={labels.tax_number} hint={TAX_NUMBER_RULES[cc]?.hint}>
            <Input
              value={draft.tax_number || ''}
              onChange={setEv('tax_number')}
              placeholder={labels.tax_number_placeholder}
              disabled={readOnly}
              data-testid="tp-tax-number"
              className={taxNumberError ? 'border-red-400 focus:ring-red-500 focus:border-red-500' : ''}
            />
            {taxNumberError && (
              <p className="text-[11px] text-red-600 mt-1" data-testid="tp-tax-number-error">{taxNumberError}</p>
            )}
          </Field>
          <Field label={labels.vat_id} hint="Optional bei Kleinunternehmer-Status">
            <Input
              value={draft.vat_id || ''}
              onChange={setEv('vat_id')}
              placeholder={labels.vat_id_placeholder}
              disabled={readOnly}
              data-testid="tp-vat-id"
            />
          </Field>
          {(cc === 'AT' || cc === 'CH') && (
            <Field label={labels.company_register_label}>
              <Input value={draft.company_register_number || ''} onChange={setEv('company_register_number')} disabled={readOnly} data-testid="tp-company-register" />
            </Field>
          )}
        </div>
      </Section>

      {/* Finanzamt */}
      <Section title={cc === 'CH' ? 'Steueramt' : 'Finanzamt'} dataTestid="tax-section-tax-office">
        <div className={FIELD_GRID}>
          <Field label="Name / Bezirk">
            <Input value={draft.tax_office_name || ''} onChange={setEv('tax_office_name')} placeholder={cc === 'AT' ? 'z.B. FA Wien 2/20/21/22' : 'z.B. Finanzamt München Abt. III'} disabled={readOnly} data-testid="tp-tax-office-name" />
          </Field>
          <Field label="Adresse (optional)">
            <Input value={draft.tax_office_address || ''} onChange={setEv('tax_office_address')} disabled={readOnly} data-testid="tp-tax-office-address" />
          </Field>
          <Field label="IBAN für Vorauszahlungen (optional)">
            <Input value={draft.tax_office_iban || ''} onChange={setEv('tax_office_iban')} disabled={readOnly} data-testid="tp-tax-office-iban" />
          </Field>
        </div>
      </Section>

      {/* Versteuerung */}
      <Section title="Versteuerung & Buchführung" dataTestid="tax-section-scheme">
        <div className={FIELD_GRID}>
          <Field label="Tätigkeitsart">
            <Select
              value={draft.business_type}
              onChange={set('business_type')}
              options={[
                { value: 'freelance', label: labels.business_freelance },
                { value: 'commercial', label: labels.business_commercial },
              ]}
              disabled={readOnly}
              testid="tp-business-type"
            />
          </Field>
          <Field
            label={(
              <>
                Versteuerungsschema
                <FachInfo
                  title="Ist- vs. Soll-Versteuerung"
                  testId="fach-info-istbesteuerung"
                  law={cc === 'AT' ? '§ 17 UStG (AT)' : '§ 20 UStG (DE)'}
                >
                  Ist-Versteuerung: Die USt wird erst fällig, wenn der Kunde tatsächlich zahlt —
                  schont die Liquidität und ist der Standard für kleine Unternehmen.
                  Soll-Versteuerung: fällig bereits mit Rechnungsstellung, auch wenn das Geld
                  noch aussteht.
                </FachInfo>
              </>
            )}
            hint="Ist = USt erst bei Geldeingang, Soll = bei Rechnungsstellung"
          >
            <Select
              value={draft.tax_scheme}
              onChange={set('tax_scheme')}
              options={[
                { value: 'cash_basis', label: 'Ist-Versteuerung (vereinnahmt)' },
                { value: 'accrual', label: 'Soll-Versteuerung (vereinbart)' },
              ]}
              disabled={readOnly}
              testid="tp-tax-scheme"
            />
          </Field>
          <Field label="Buchführungs-Methode">
            <Select
              value={draft.accounting_method}
              onChange={set('accounting_method')}
              options={accOptions}
              disabled={readOnly}
              testid="tp-accounting-method"
            />
          </Field>
          <Field label={`${countryConfig?.voranmeldung_label || 'Voranmeldung'}-Frequenz`}>
            <Select
              value={draft.vat_filing_frequency}
              onChange={set('vat_filing_frequency')}
              options={[
                { value: 'monthly', label: VAT_FREQ_LABELS.monthly },
                { value: 'quarterly', label: VAT_FREQ_LABELS.quarterly },
                { value: 'yearly', label: VAT_FREQ_LABELS.yearly },
                { value: 'exempt', label: VAT_FREQ_LABELS.exempt },
              ]}
              disabled={readOnly}
              testid="tp-vat-frequency"
            />
          </Field>
          <Field label="Geschäftsjahr-Beginn (MM-DD)" hint="Default Kalenderjahr 01-01">
            <Input value={draft.fiscal_year_start || '01-01'} onChange={setEv('fiscal_year_start')} disabled={readOnly} data-testid="tp-fiscal-year" />
          </Field>
          <Field label="Zusammenfassende Meldung (ZM) bei EU-B2B">
            <label className="flex items-center gap-2 mt-2">
              <input type="checkbox" checked={!!draft.vat_zm_required} onChange={(e) => set('vat_zm_required')(e.target.checked)} disabled={readOnly} data-testid="tp-zm-required" />
              <span className="text-sm text-slate-700">Ja, ZM-pflichtig</span>
            </label>
          </Field>
        </div>
      </Section>

      {/* Bank */}
      <Section title="Bank" dataTestid="tax-section-bank">
        <div className={FIELD_GRID}>
          <Field label="IBAN">
            <Input value={draft.iban || ''} onChange={setEv('iban')} disabled={readOnly} data-testid="tp-iban" />
          </Field>
          <Field label="BIC">
            <Input value={draft.bic || ''} onChange={setEv('bic')} disabled={readOnly} data-testid="tp-bic" />
          </Field>
          <Field label="Bankname">
            <Input value={draft.bank_name || ''} onChange={setEv('bank_name')} disabled={readOnly} data-testid="tp-bank-name" />
          </Field>
        </div>
      </Section>

      {/* Save */}
      {!readOnly && (
        <div className="flex items-center justify-end pt-4 border-t border-zinc-100">
          <Button onClick={submit} disabled={!dirty || saving || !!taxNumberError} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="tp-save">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
            Stammdaten speichern
          </Button>
        </div>
      )}
    </div>
  );
}

export default TaxProfileForm;
